import React, { useState } from 'react';
import { operationLogger, OperationType, LogLevel } from '../utils/operationLogger';
import { useAuth } from '../hooks/useAuth';
import { addEpisodeFavorite, removeEpisodeFavorite, getAllFavorites } from '../api/fancaps';

/**
 * 操作日志测试页面
 * 用于测试和演示操作日志记录功能
 */
export const OperationLogTestPage: React.FC = () => {
  const { user, login, logout } = useAuth();
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const addTestResult = (message: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const clearResults = () => {
    setTestResults([]);
    operationLogger.clearHistory();
  };

  // 测试基础日志功能
  const testBasicLogging = () => {
    addTestResult('开始测试基础日志功能');
    
    operationLogger.debug(OperationType.USER_CLICK, '这是一条调试日志', { test: true });
    operationLogger.info(OperationType.USER_INPUT, '这是一条信息日志', { test: true });
    operationLogger.warn(OperationType.API_ERROR, '这是一条警告日志', { test: true });
    operationLogger.error(OperationType.API_ERROR, '这是一条错误日志', { test: true });
    operationLogger.success(OperationType.USER_CLICK, '这是一条成功日志', { test: true });
    
    addTestResult('基础日志测试完成，请查看控制台');
  };

  // 测试用户操作日志
  const testUserActionLogging = () => {
    addTestResult('开始测试用户操作日志');
    
    operationLogger.logUserAction('点击测试按钮', '操作日志测试页面', {
      buttonType: 'test',
      pageUrl: window.location.href,
      userAgent: navigator.userAgent
    });
    
    operationLogger.logUserAction('输入测试数据', '测试输入框', {
      inputType: 'text',
      inputLength: 10
    });
    
    addTestResult('用户操作日志测试完成，请查看控制台');
  };

  // 测试API请求日志
  const testAPILogging = async () => {
    setIsLoading(true);
    addTestResult('开始测试API请求日志');
    
    try {
      // 模拟API请求
      const requestId = operationLogger.logAPIRequest('GET', '/api/test', { test: true });
      
      // 模拟延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 模拟成功响应
      operationLogger.logAPIResponse(requestId, 200, { success: true }, {}, 1000);
      
      addTestResult('API请求日志测试完成（成功响应）');
      
      // 测试错误响应
      const errorRequestId = operationLogger.logAPIRequest('POST', '/api/error-test', { test: true });
      operationLogger.logAPIError(errorRequestId, new Error('测试错误'), 500);
      
      addTestResult('API错误日志测试完成');
    } catch (error) {
      addTestResult(`API日志测试失败: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 测试收藏操作日志
  const testFavoriteLogging = () => {
    addTestResult('开始测试收藏操作日志');
    
    operationLogger.logFavoriteOperation('add', 'episode', 'test-episode-123', '测试剧集标题');
    operationLogger.logFavoriteOperation('remove', 'work', 'test-work-456', '测试作品标题');
    operationLogger.logFavoriteOperation('add', 'image', 'test-image-789', '测试图片标题');
    
    addTestResult('收藏操作日志测试完成，请查看控制台');
  };

  // 测试下载操作日志
  const testDownloadLogging = () => {
    addTestResult('开始测试下载操作日志');
    
    operationLogger.logDownloadOperation('start', {
      fileName: 'test-image.jpg',
      fileSize: 1024000,
      url: 'https://example.com/test-image.jpg'
    });
    
    operationLogger.logDownloadOperation('progress', {
      fileName: 'test-image.jpg',
      progress: 50,
      downloadedBytes: 512000,
      totalBytes: 1024000
    });
    
    operationLogger.logDownloadOperation('success', {
      fileName: 'test-image.jpg',
      finalSize: 1024000,
      duration: 5000
    });
    
    addTestResult('下载操作日志测试完成，请查看控制台');
  };

  // 测试页面导航日志
  const testNavigationLogging = () => {
    addTestResult('开始测试页面导航日志');
    
    operationLogger.logPageNavigation('/search', '/episodes/123');
    operationLogger.logPageNavigation('/episodes/123', '/images/456');
    operationLogger.logPageNavigation('/images/456', '/favorites');
    
    addTestResult('页面导航日志测试完成，请查看控制台');
  };

  // 测试认证操作日志（如果用户已登录）
  const testAuthLogging = async () => {
    if (!user) {
      addTestResult('请先登录以测试认证日志');
      return;
    }
    
    setIsLoading(true);
    addTestResult('开始测试认证操作日志');
    
    try {
      // 这里只是记录日志，不实际执行登出
      operationLogger.info(OperationType.AUTH_CHECK, '测试认证状态检查', {
        userId: user.id,
        email: user.email,
        isAuthenticated: true
      });
      
      addTestResult('认证操作日志测试完成，请查看控制台');
    } catch (error) {
      addTestResult(`认证日志测试失败: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 显示日志统计
  const showLogStats = () => {
    operationLogger.showStats();
    addTestResult('日志统计已显示在控制台');
  };

  // 导出日志
  const exportLogs = () => {
    const logs = operationLogger.exportLogs('json');
    const blob = new Blob([logs], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `operation-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    addTestResult('日志已导出为JSON文件');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">操作日志测试页面</h1>
            <p className="mt-2 text-sm text-gray-600">
              此页面用于测试操作日志记录功能。请打开浏览器控制台查看详细的日志输出。
            </p>
          </div>
          
          <div className="p-6">
            {/* 用户状态 */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-medium text-blue-900 mb-2">当前用户状态</h3>
              {user ? (
                <div className="text-sm text-blue-700">
                  <p>已登录用户: {user.email}</p>
                  <p>用户ID: {user.id}</p>
                </div>
              ) : (
                <p className="text-sm text-blue-700">未登录</p>
              )}
            </div>
            
            {/* 测试按钮组 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <button
                onClick={testBasicLogging}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                测试基础日志
              </button>
              
              <button
                onClick={testUserActionLogging}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                测试用户操作日志
              </button>
              
              <button
                onClick={testAPILogging}
                disabled={isLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? '测试中...' : '测试API日志'}
              </button>
              
              <button
                onClick={testFavoriteLogging}
                className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 transition-colors"
              >
                测试收藏日志
              </button>
              
              <button
                onClick={testDownloadLogging}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
              >
                测试下载日志
              </button>
              
              <button
                onClick={testNavigationLogging}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                测试导航日志
              </button>
              
              <button
                onClick={testAuthLogging}
                disabled={!user || isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                测试认证日志
              </button>
              
              <button
                onClick={showLogStats}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                显示日志统计
              </button>
              
              <button
                onClick={exportLogs}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
              >
                导出日志
              </button>
              
              <button
                onClick={() => {
                  // 模拟详细的收藏操作失败流程
                  const processId = operationLogger.startStepProcess('添加剧集收藏: Test Episode', 3);
                  
                  // 步骤1: 用户操作
                  operationLogger.logStep(processId, 1, '用户点击收藏按钮', {
                    action: 'add_favorite',
                    episodeTitle: 'Test Episode',
                    episodeUrl: 'test-url',
                    workTitle: 'Test Work',
                    currentState: '未收藏',
                    targetState: '添加收藏'
                  }, 'success');
                  
                  // 步骤2: 准备API请求
                  operationLogger.logStep(processId, 2, '准备收藏API请求', {
                    operation: 'add',
                    episodeData: {
                      title: 'Test Episode',
                      url: 'test-url',
                      imageCount: 25
                    }
                  }, 'start');
                  
                  // 步骤3: API请求失败
                  const mockError = {
                    name: 'APIError',
                    message: 'Not authenticated',
                    response: {
                      status: 401,
                      statusText: 'Unauthorized',
                      data: { error: 'Token expired' }
                    },
                    config: {
                      method: 'POST',
                      url: '/api/favorites/episode',
                      headers: { 'Authorization': 'Bearer [EXPIRED_TOKEN]' }
                    }
                  };
                  
                  operationLogger.logStep(processId, 3, 'API请求失败', {
                    error: mockError.message,
                    errorType: mockError.name,
                    statusCode: mockError.response.status
                  }, 'error');
                  
                  // 记录详细的API错误
                  const requestId = `favorite_add_${Date.now()}`;
                  operationLogger.logAPIError(requestId, mockError);
                  
                  // 完成步骤流程（失败）
                  operationLogger.finishStepProcess(processId, false, '添加收藏操作失败: Not authenticated');
                  
                  // 传统的收藏操作日志
                  operationLogger.logFavoriteOperation('add', 'episode', 'test-url', 'Test Episode');
                  operationLogger.error(OperationType.FAVORITE_ADD_EPISODE, '收藏操作失败: APIError: Not authenticated');
                  
                  addTestResult('详细收藏失败流程测试完成');
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                模拟详细收藏失败
              </button>
              
              <button
                onClick={() => {
                  // 模拟认证流程失败
                   operationLogger.logAuthFlow('login_start', {
                     username: 'test@example.com',
                     loginMethod: 'email'
                   }, 'start');
                   
                   operationLogger.logAuthFlow('login_validation_failed', {
                     error: 'Invalid credentials',
                     attempts: 1
                   }, 'error');
                   
                   operationLogger.logAuthFlow('login_failed', {
                     finalError: 'Authentication failed after 1 attempt'
                   }, 'error');
                  
                  addTestResult('认证失败流程测试完成');
                }}
                className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
              >
                模拟认证失败
              </button>
              
              <button
                onClick={() => {
                  // 模拟成功的分步骤操作
                  const processId = operationLogger.startStepProcess('下载图片', 4);
                  
                  operationLogger.logStep(processId, 1, '验证图片URL', {
                    imageUrl: 'https://example.com/image.jpg',
                    imageSize: '1920x1080'
                  }, 'success');
                  
                  operationLogger.logStep(processId, 2, '开始下载', {
                    downloadMethod: 'fetch',
                    expectedSize: '2.5MB'
                  }, 'start');
                  
                  setTimeout(() => {
                    operationLogger.logStep(processId, 3, '下载进度', {
                       progress: '50%',
                       downloadedBytes: '1.25MB'
                     }, 'start');
                    
                    setTimeout(() => {
                      operationLogger.logStep(processId, 4, '下载完成', {
                        finalSize: '2.48MB',
                        duration: '3.2s'
                      }, 'success');
                      
                      operationLogger.finishStepProcess(processId, true, '图片下载成功完成');
                      addTestResult('成功流程测试完成');
                    }, 1000);
                  }, 1500);
                }}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
              >
                模拟成功流程
              </button>
            </div>
            
            {/* 控制按钮 */}
            <div className="flex space-x-4 mb-6">
              <button
                onClick={clearResults}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                清除结果和日志历史
              </button>
              
              <button
                onClick={() => operationLogger.setEnabled(!operationLogger.getEnabled())}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                {operationLogger.getEnabled() ? '禁用' : '启用'}日志记录
              </button>
            </div>
            
            {/* 测试结果 */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">测试结果</h3>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {testResults.length === 0 ? (
                  <p className="text-gray-500 text-sm">暂无测试结果</p>
                ) : (
                  testResults.map((result, index) => (
                    <div key={index} className="text-sm text-gray-700 font-mono">
                      {result}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* 使用说明 */}
            <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
              <h3 className="text-lg font-medium text-yellow-900 mb-2">使用说明</h3>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• 点击上方按钮测试不同类型的日志记录功能</li>
                <li>• 打开浏览器控制台（F12）查看详细的日志输出</li>
                <li>• 日志会以不同颜色和图标显示，便于区分不同类型的操作</li>
                <li>• 可以使用 window.operationLogger 在控制台中直接访问日志记录器</li>
                <li>• 支持导出日志为JSON文件，便于分析和调试</li>
              </ul>
            </div>
            
            {/* 控制台命令 */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-medium text-blue-900 mb-2">控制台命令</h3>
              <div className="text-sm text-blue-700 font-mono space-y-1">
                <div>operationLogger.showStats() - 显示统计信息</div>
                <div>operationLogger.getLogHistory() - 获取日志历史</div>
                <div>operationLogger.exportLogs() - 导出日志</div>
                <div>operationLogger.clearHistory() - 清除历史</div>
                <div>operationLogger.setEnabled(false) - 禁用日志</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationLogTestPage;